import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useListAllEvChargersQuery } from "../../../redux/api/ev-charger/evChargerAPI";
import Reveal from "../../../shared/components/reveal/reveal";
import useDebounce from "../../../shared/hooks/useDebounce";
import { IPowerConsumerView } from "../../../shared/oversight-core/interfaces/entities/power-consumer";
import AppButton from "../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import Search from "../../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import DeviceCard from "./components/device-card/device-card";
import AddUpdateDeviceModal from "./components/modals/add-update-device-modal/add-update-device-modal";

const MIN_AUTO_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

const Devices = () => {
  const [searchText, setSearchText] = useState("");
  const [appliedSearchText, setAppliedSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_MS);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [deviceToEdit, setDeviceToEdit] = useState<IPowerConsumerView | null>(
    null,
  );

  const { data, isFetching, refetch } = useListAllEvChargersQuery({
    searchField: appliedSearchText ? "name" : undefined,
    searchValue: appliedSearchText || undefined,
  });

  const evChargers = data?.evChargers || [];

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    if (!text.trim()) {
      setAppliedSearchText("");
    }
  };

  // auto-search once the user has typed enough to narrow the list; shorter
  // queries can still be submitted explicitly with Enter or the search button
  useEffect(() => {
    const trimmed = debouncedSearchText.trim();
    if (trimmed.length >= MIN_AUTO_SEARCH_LENGTH) {
      setAppliedSearchText(trimmed);
    }
  }, [debouncedSearchText]);

  const handleSearch = () => {
    setAppliedSearchText(searchText.trim());
  };

  const handleOpenAddModal = () => {
    setDeviceToEdit(null);
    setShowAddDeviceModal(true);
  };

  const handleEditDevice = (device: IPowerConsumerView) => {
    setDeviceToEdit(device);
    setShowAddDeviceModal(true);
  };

  const handleCloseModal = () => {
    setShowAddDeviceModal(false);
    setDeviceToEdit(null);
  };

  return (
    <div className="position-relative">
      <Reveal>
        <Row className="align-items-center g-4 mb-3">
          <Col className="col col-sm-7 col-md-6 col-lg-5 col-xl-4 col-xxl-3">
            <Search
              value={searchText}
              handleSearchChange={handleSearchChange}
              onSearch={handleSearch}
              placeholder="Search Device Name"
            />
          </Col>
          <Col className="col-auto">
            <Row className="d-none d-sm-block">
              <Col>
                <AppButton
                  text="Add Device"
                  onClick={handleOpenAddModal}
                  type="button"
                  variant="secondary"
                />
              </Col>
            </Row>
            <Row className="d-block d-sm-none">
              <Col>
                <AppButton
                  iconOnly
                  iconName="add"
                  text="Add Device"
                  onClick={handleOpenAddModal}
                  type="button"
                  variant="secondary"
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Reveal>
      <Row className="g-3 mt-1">
        {!isFetching && evChargers.length === 0 ? (
          <Col xs={12}>
            <Reveal>
              <div className="container-dash text-center font-size-16 text-light p-4">
                No EV chargers found.
              </div>
            </Reveal>
          </Col>
        ) : (
          evChargers.map((device, index) => (
            <Col className="col-6 col-sm-4 col-lg-3" key={device.id}>
              <Reveal delay={Math.min(index, 10) * 55} className="h-100">
                <DeviceCard
                  device={device}
                  onEdit={handleEditDevice}
                  onRemoveSuccess={refetch}
                />
              </Reveal>
            </Col>
          ))
        )}
      </Row>
      <AddUpdateDeviceModal
        show={showAddDeviceModal}
        deviceToEdit={deviceToEdit}
        onClose={handleCloseModal}
        onCancel={handleCloseModal}
        onSuccess={refetch}
      />
      <SpinnerModal show={isFetching} />
    </div>
  );
};

export default Devices;
